import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, TextField, Typography } from '@mui/material';
import {
  api,
  CpdTeamLeader,
  ShareLinkAnalyticsQuery,
  TeamShareLinkAnalyticsResponse,
} from '../../services/api';
import ShareLinksAnalyticsTab from '../admin/share-links-analytics/ShareLinksAnalyticsTab';
import { formatInt, formatPercent, ratio } from '../admin/share-links-analytics/shareLinksAnalyticsModel';
import StatisticsDialog from './StatisticsDialog';

const leaderLabel = (leader: CpdTeamLeader) => `${leader.name || leader.ceaNumber} · ${leader.ceaNumber}`;

/**
 * The Shareable Links dashboard over one leader's team — everyone down their
 * reporting line within their division, not just direct reports. The server
 * resolves the team from the session; admins may pick another leader.
 */
const TeamShareLinksTab: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const [leaders, setLeaders] = useState<CpdTeamLeader[]>([]);
  const [leadersError, setLeadersError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CpdTeamLeader | null>(null);

  // Only admins choose a team; a leader's own needs no lookup.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    api.getCpdTeamLeaders()
      .then((res) => { if (!cancelled) setLeaders(res.leaders); })
      .catch((err) => {
        if (!cancelled) setLeadersError(err instanceof Error ? err.message : 'Failed to load leaders');
      });
    return () => { cancelled = true; };
  }, [isAdmin]);

  const team = selected?.ceaNumber;
  const fetchAnalytics = useCallback(
    (query: ShareLinkAnalyticsQuery) => api.getTeamShareLinkAnalytics({ ...query, team }),
    [team],
  );

  return (
    <>
      {leadersError ? <Alert severity="warning">{leadersError}</Alert> : null}
      <ShareLinksAnalyticsTab<TeamShareLinkAnalyticsResponse>
        fetchAnalytics={fetchAnalytics}
        toolbarStart={isAdmin ? (
          <Autocomplete
            size="small"
            options={leaders}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={leaderLabel}
            isOptionEqualToValue={(a, b) => a.ceaNumber === b.ceaNumber}
            renderOption={(props, leader) => (
              <Box component="li" {...props} key={leader.ceaNumber}>
                <Box>
                  <Typography variant="body2">{leaderLabel(leader)}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    {[leader.designation, leader.division].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            )}
            sx={{ width: 320 }}
            renderInput={(params) => <TextField {...params} label="Team of" placeholder="My own team" />}
          />
        ) : null}
        scopeCaption={(data) => {
          const who = data.scope.leader.name || data.scope.leader.ceaNumber;
          return `Team of ${who} · ${formatInt(data.scope.teamSize)} ${data.scope.teamSize === 1 ? 'agent' : 'agents'}`;
        }}
        teamSize={(data) => data.scope.teamSize}
        exportScope={(data) => ({
          label: `Team of ${data.scope.leader.name || ''} (${data.scope.leader.ceaNumber}), ${data.scope.teamSize} agents, `
            + `${formatPercent(ratio(data.totals.sharers, data.scope.teamSize))} sharing`,
          fileTag: `team-${data.scope.leader.ceaNumber}`,
        })}
      />
    </>
  );
};

interface LeaderStatisticsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Admins can view any leader's team. */
  isAdmin?: boolean;
}

/** Leader → Statistics. Shareable Links is the first tab; more team views can join it. */
const LeaderStatisticsDialog: React.FC<LeaderStatisticsDialogProps> = ({ open, onClose, isAdmin = false }) => (
  <StatisticsDialog
    open={open}
    onClose={onClose}
    title="Statistics"
    subtitle="Your team's activity — everyone down your reporting line, not only your direct reports."
    tabs={[
      { value: 'shareLinks', label: 'Shareable Links', render: () => <TeamShareLinksTab isAdmin={isAdmin} /> },
    ]}
  />
);

export default LeaderStatisticsDialog;
